import { useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { ReactNode } from 'react'
import { useAccessibility } from '../../hooks/useAccessibility'

// Global modal counter to handle multiple modals correctly
let modalCount = 0
let originalBodyOverflow = ''

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showCloseButton?: boolean
  closeOnBackdropClick?: boolean
  closeOnEscape?: boolean
  initialFocusRef?: React.RefObject<HTMLElement>
  finalFocusRef?: React.RefObject<HTMLElement>
  ariaLabelledBy?: string
  ariaDescribedBy?: string
}

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  initialFocusRef,
  finalFocusRef,
  ariaLabelledBy,
  ariaDescribedBy
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const previouslyFocusedElement = useRef<HTMLElement | null>(null)
  const { createFocusTrap, announce } = useAccessibility()

  // Manage body scroll and focus
  useEffect(() => {
    if (isOpen) {
      // Store currently focused element
      previouslyFocusedElement.current = document.activeElement as HTMLElement

      // Handle body scroll prevention with modal counter
      modalCount++
      if (modalCount === 1) {
        // Store original overflow value and prevent scroll only for first modal
        originalBodyOverflow = document.body.style.overflow || ''
        document.body.style.overflow = 'hidden'
      }

      // Announce modal opening to screen readers
      announce(title ? `Modal abierto: ${title}` : 'Modal abierto', 'polite')

      // Set up focus trap
      let cleanup: (() => void) | undefined
      if (modalRef.current) {
        cleanup = createFocusTrap(modalRef.current)
      }

      // Focus initial element or modal title or first focusable element
      setTimeout(() => {
        if (initialFocusRef?.current) {
          initialFocusRef.current.focus()
        } else if (modalRef.current) {
          // First try to focus the title if it exists
          const titleElement = modalRef.current.querySelector('#modal-title') as HTMLElement
          if (titleElement) {
            titleElement.focus()
          } else {
            // Otherwise focus first focusable element
            const firstFocusable = modalRef.current.querySelector(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            ) as HTMLElement
            if (firstFocusable) {
              firstFocusable.focus()
            }
          }
        }
      }, 100)

      return () => {
        cleanup?.()
        // Decrease modal count when modal unmounts while open
        modalCount--
        if (modalCount <= 0) {
          modalCount = 0
          // Restore original overflow only when all modals are closed
          document.body.style.overflow = originalBodyOverflow
        }
      }
    } else {
      // Decrease modal count and restore body scroll if this was the last modal
      modalCount--
      if (modalCount <= 0) {
        modalCount = 0
        // Restore original overflow value instead of using 'unset'
        document.body.style.overflow = originalBodyOverflow
      }

      // Return focus to previously focused element
      const elementToFocus = finalFocusRef?.current || previouslyFocusedElement.current
      if (elementToFocus) {
        setTimeout(() => {
          elementToFocus.focus()
        }, 100)
      }
    }
  }, [isOpen, initialFocusRef, finalFocusRef, title, createFocusTrap, announce])

  // Handle keyboard interactions
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) {
        e.preventDefault()
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose, closeOnEscape])

  const handleBackdropClick = () => {
    if (closeOnBackdropClick) {
      onClose()
    }
  }

  if (!isOpen) return null

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby={ariaLabelledBy || (title ? 'modal-title' : undefined)}
          aria-describedBy={ariaDescribedBy}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleBackdropClick}
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            ref={modalRef}
            className={`relative bg-white w-full h-full sm:h-auto sm:max-h-[95vh] sm:rounded-2xl shadow-2xl sm:${sizeClasses[size]} overflow-hidden flex flex-col`}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 1, y: "100%" }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 1, y: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {(title || showCloseButton) && (
              <header className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                {title && (
                  <h1
                    id={ariaLabelledBy || 'modal-title'}
                    className="text-lg sm:text-xl font-semibold text-gray-900"
                    tabIndex={-1}
                  >
                    {title}
                  </h1>
                )}
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 focus:text-gray-600 transition-colors p-2 -m-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    aria-label="Cerrar ventana modal"
                    type="button"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </header>
            )}

            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// Enhanced Modal with full accessibility support
const AccessibleModal = Modal

export default AccessibleModal
export { Modal }